import type {
  NotificationListResponse,
  NotificationResponse,
  UnreadExistsResponse,
  NotificationData,
} from "@repo/share-log-interfaces";
import type { NotificationType } from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const NotificationTypeSchema = z.string() as z.ZodType<NotificationType>;

const NotificationDataSchema: z.ZodType<NotificationData> = z.object({
  landingPath: z.string().optional(),
  entityId: z.number().optional(),
  entityType: z.string().optional(),
});

const NotificationSchema: z.ZodType<NotificationResponse> = z.object({
  id: z.number(),
  userId: z.number(),
  workspaceId: z.number().nullish(),
  type: NotificationTypeSchema,
  title: z.string(),
  body: z.string(),
  isRead: z.boolean(),
  data: NotificationDataSchema.nullish(),
  createdAt: z.string(),
});

const NotificationListResponseSchema: z.ZodType<NotificationListResponse> =
  z.object({
    notifications: z.array(NotificationSchema),
    nextCursor: z.string().optional(),
    hasMore: z.boolean(),
  });

const UnreadExistsResponseSchema: z.ZodType<UnreadExistsResponse> = z.object({
  hasUnread: z.boolean(),
});

// Response Schemas
const ListNotificationsResponseSchema: z.ZodType<
  DataAndMessageResponse<NotificationListResponse>
> = z.object({
  data: NotificationListResponseSchema,
  message: z.string(),
});

const CheckUnreadResponseSchema: z.ZodType<
  DataAndMessageResponse<UnreadExistsResponse>
> = z.object({
  data: UnreadExistsResponseSchema,
  message: z.string(),
});

const MarkAsReadResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const MarkAllAsReadResponseSchema: z.ZodType<
  DataAndMessageResponse<{ count: number }>
> = z.object({
  data: z.object({ count: z.number() }),
  message: z.string(),
});

export function createNotificationModule(httpClient: HttpClient) {
  return {
    list: async (cursor?: string, limit: number = 20) => {
      const params = new URLSearchParams();
      if (cursor) params.set("cursor", cursor);
      params.set("limit", String(limit));

      const response = await httpClient.get<
        typeof ListNotificationsResponseSchema
      >(`/api/v1/notifications?${params.toString()}`, {
        schema: ListNotificationsResponseSchema,
      });
      return response.data;
    },

    checkUnread: async () => {
      const response = await httpClient.get<typeof CheckUnreadResponseSchema>(
        "/api/v1/notifications/unread-exists",
        { schema: CheckUnreadResponseSchema },
      );
      return response.data;
    },

    markAsRead: async (id: number) => {
      const response = await httpClient.patch<typeof MarkAsReadResponseSchema>(
        `/api/v1/notifications/${id}/read`,
        { schema: MarkAsReadResponseSchema },
      );
      return response;
    },

    markAllAsRead: async () => {
      const response = await httpClient.patch<
        typeof MarkAllAsReadResponseSchema
      >("/api/v1/notifications/read-all", {
        schema: MarkAllAsReadResponseSchema,
      });
      return response.data;
    },
  };
}
